<script lang="ts" setup>
import { ref, computed } from 'vue'
import { PhStar } from '@phosphor-icons/vue'
import { BadgeLabel } from '@pkg/ui/components/ui/badge'
import { Modal } from '@pkg/ui/components/ui/modal'
import type { ItemTagBadge } from '@pkg/utilities/surprisebag'
import { getTagIllustration } from './tagIllustrations'

const props = withDefaults(defineProps<{
  /** Localized overlay badges built from the API's `itemTags`, in priority order. */
  tags?: ItemTagBadge[] | undefined;
  ratings?: number | undefined;
  logo?: string | undefined;
  image?: string | undefined;
  altText?: string | 'Too Good To Go bag image';
  availableBag?: boolean | undefined;
  logoAltText?: string | 'Logo';
  tagModalCtaText?: string | undefined;
  tagsClickable?: boolean | undefined;
}>(), {
  tags: () => [],
  tagModalCtaText: '',
  tagsClickable: true,
})

/**
 * The API sends `description` on some tags and `longText` on others, so the
 * body falls back to `longText`; when both are present the `longText` goes in
 * the note bar underneath instead of being dropped.
 */
const modalCopyFor = (tag: ItemTagBadge) => ({
  description: tag.description || tag.longText || '',
  note: tag.description && tag.longText ? tag.longText : '',
})

const ratingLabel = computed(() =>
  props.ratings != null && props.ratings > 0 ? props.ratings.toFixed(1) : ''
);
const showRatingBadge = computed(() => ratingLabel.value !== '');

const imageLoaded = ref(false)
const logoLoaded = ref(false)

const imageContainerClass = computed(() => ({
  'bag-header__image--fade': !props.availableBag,
  loaded: imageLoaded.value
}))

const vImgLoaded = {
  mounted(el: HTMLImageElement, binding: any) {
    const cb = binding.value

    if (el.complete) {
      cb(true)
      return
    }

    el.addEventListener('load', () => cb(true))
    el.addEventListener('error', () => cb(false))
  },

  unmounted(el: HTMLImageElement, binding: any) {
    const cb = binding.value
    el.removeEventListener('load', cb)
    el.removeEventListener('error', cb)
  }
}

function handleImgLoaded(success: boolean) {
  imageLoaded.value = success
}

const tagHasPopUp = (tag: ItemTagBadge) => (
  props.tagsClickable && (tag.description || tag.longText) && getTagIllustration(tag.tagVariant)
);

</script>
<template>
  <figure class="bag-header bag-header__image__wrapper" :class="imageContainerClass">
    <NuxtImg
      v-img-loaded="handleImgLoaded"
      :src="image|| '/images/logo.svg'"
      :alt="altText"
      class="bag-header__image"
      loading="eager"
      fetchpriority="high"
    />
    
    <div v-if="props.tags.length" class="bag-header__tag bag-header__tag__top-left">
      <template v-for="tag in props.tags" :key="tag.key">
        <Modal
          v-if="tagHasPopUp(tag)"
          :title="tag.label"
          :description="modalCopyFor(tag).description"
          :note="modalCopyFor(tag).note"
          :cta-text="tagModalCtaText"
          :illustration-icon="getTagIllustration(tag.tagVariant)?.icon"
          :illustration-blob="getTagIllustration(tag.tagVariant)?.blob"
          :illustration-tone="getTagIllustration(tag.tagVariant)?.tone"
        >
          <template #trigger>
            <BadgeLabel :variant="tag.variant" size="medium">
              {{ tag.label }}
            </BadgeLabel>
          </template>
        </Modal>
        <BadgeLabel v-else :variant="tag.variant" size="medium">
          {{ tag.label }}
        </BadgeLabel>
      </template>
    </div>
    
    <BadgeLabel v-if="showRatingBadge" variant="base" size="medium" :icon-leading="PhStar" class="bag-header__tag bag-header__tag__top-right"
    tabindex="0">
      {{ ratingLabel }}
    </BadgeLabel>
    <span class="bag-header__tag bag-header__tag__logo bag-header__tag__bottom-left" tabindex="0">
      <figure>
        <div
          v-if="!imageLoaded"
          class="rounded-full bag-header__tag__logo__placeholder"
        ></div>

        <img
          v-else
          class="rounded-full"
          @error="logoLoaded = false"
          :src="props.logo"
          :alt="logoAltText"
        >
      </figure>
    </span>

  </figure>
</template>

<style>
@import '../../../assets/css/mixins/_mixins.index.pcss';

.bag-header {

    position: relative;
    width: 100vw;
    overflow: hidden;
    height: 100px;
    border-radius: 8px;
    background-color: theme("colors.green.40") !important;
    background-repeat: no-repeat;
    background-position: center;
    background-image: url('/images/logo.svg');

    &.loaded {

      .bag-header__image {
        opacity: 1;
      }

      &.bag-header__image--fade {
          &:after {
            background-color: theme("colors.white");
            content: '';
            display: block;
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            opacity: 0.5;
            z-index: 1;
          }
        }
    }

    &__image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0;
      transition: opacity .5s ease-in-out; 
    }
}

.bag-header__tag {
  position: absolute;
  
  &__logo {
    width: 64px;
    height: 64px;

    @screen tablet-wide {
      width: 60px;
      height: 60px;
    }

    &__placeholder {
      background-image: url('../../../../cms-icons/assets/sprout-placeholder-logo.svg');
      background-size: cover;
      background-position: center;
      width: 64px;
      height: 64px;
    }
  }

  &__top-left {
    top: 8px;
    left: 8px;
    display: flex;
    gap: 4px;
    /* Several tags can stack up, so wrap instead of running under the rating badge. */
    flex-wrap: wrap;
    max-width: calc(100% - 80px);

    & > button {
      appearance: none;
      display: inline-flex;
      background: none;
      border: 0;
      padding: 0;
      font: inherit;
      color: inherit;
      cursor: pointer;
    }

    @screen tablet-wide {
      top: 10px;
      left: 10px;
    }
  }

  &__top-right {
    top: 8px;
    right: 8px;

    @screen tablet-wide {
      top: 10px;
      right: 10px;
    }
  }

  &__bottom-left {
    bottom: 8px;
    left: 8px;

    @screen tablet-wide {
      bottom: 10px;
      left: 10px;
    }
  }

  &__logo,
  &__top-left,
  &__top-right,
  &__bottom-left {
    z-index: theme('zIndex.page');
  }
}

.screen-reader-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
